import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import LoadingSpinner from '../components/LoadingSpinner';
import groupService from '../services/GroupService';
import { debugError } from '../utils/debugUtils';

export default function GroupJoinPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromUrl = (searchParams.get('code') || '').trim().toUpperCase();

  const [code, setCode] = useState(codeFromUrl);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(!!codeFromUrl);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!codeFromUrl) return;
    setLoading(true);
    setError('');
    groupService
      .previewInvitation(codeFromUrl)
      .then((data) => setPreview(data))
      .catch((err) => {
        debugError('[GroupJoinPage] Error loading invitation preview:', err);
        setError(t(`apiErrors.${err.code}`, t('groups.invalidCode')));
      })
      .finally(() => setLoading(false));
  }, [codeFromUrl]);

  const handleLookup = () => {
    if (!code.trim()) return;
    navigate(`/groups/join?code=${encodeURIComponent(code.trim().toUpperCase())}`);
  };

  const handleJoin = async () => {
    setJoining(true);
    setError('');
    try {
      const group = await groupService.joinViaInvitation(codeFromUrl);
      navigate(`/groups/${group.id}`, { replace: true });
    } catch (err) {
      setError(t(`apiErrors.${err.code}`, t('common.error')));
    } finally {
      setJoining(false);
    }
  };

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 500, mx: 'auto', width: '100%' }}>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        color="white"
        sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {t('groups.joinTitle')}
      </Typography>

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!codeFromUrl && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={t('groups.inviteCode')}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                fullWidth
                size="small"
              />
              <Button variant="contained" onClick={handleLookup} disabled={!code.trim()}>
                {t('common.confirm')}
              </Button>
            </Box>
          )}

          {loading && <LoadingSpinner />}

          {!loading && error && (
            <Typography color="error" textAlign="center">
              {error}
            </Typography>
          )}

          {!loading && !error && preview && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700}>
                {preview.name}
              </Typography>
              {preview.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {preview.description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('groups.membersCount', { count: preview.memberCount })}
              </Typography>
              <Button variant="contained" fullWidth onClick={handleJoin} disabled={joining}>
                {t('groups.joinConfirmButton')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
